import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { rpc } from '../../api/rpc'
import type { Connector } from '../../api/types'
import { Button } from '../ui/Button'
import { Field, Input, Segmented } from '../ui/Field'
import { Select } from '../ui/Select'
import { useToast } from '../ui/Toast'
import { IconClose, IconPlus } from '../icons'

/**
 * MCP server form (docs/connectors.md §MCP): remote servers take a URL, an authentication mode and
 * any number of custom request headers (API keys, tenant ids…); local servers take a command, args
 * and env. Header and env values are secrets: when editing, the server never returns them, so an
 * empty value means "keep the stored one".
 */

export interface KvRow { key: string; value: string }

const toObject = (rows: KvRow[]): Record<string, string> =>
  Object.fromEntries(rows.filter(r => r.key.trim()).map(r => [r.key.trim(), r.value]))

/** Parses the JSON view; returns null when it is not a flat {"name": "value"} object. */
export function parseKvJson(text: string): KvRow[] | null {
  if (!text.trim()) return []
  try {
    const v: unknown = JSON.parse(text)
    if (!v || typeof v !== 'object' || Array.isArray(v)) return null
    const rows: KvRow[] = []
    for (const [key, value] of Object.entries(v)) {
      if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') return null
      rows.push({ key, value: String(value) })
    }
    return rows
  } catch {
    return null
  }
}

/** Key/value editor with a table view and a raw JSON view (both edit the same rows). */
export function KeyValueEditor({ rows, onChange, keyLabel, valueLabel, addLabel, keyPlaceholder, valuePlaceholder, storedKeys = [], onJsonError }: {
  rows: KvRow[]; onChange(rows: KvRow[]): void
  keyLabel: string; valueLabel: string; addLabel: string; keyPlaceholder?: string; valuePlaceholder?: string
  /** Keys whose value is already stored: an empty value keeps it. */
  storedKeys?: string[]
  onJsonError?(invalid: boolean): void
}) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'rows' | 'json'>('rows')
  const [json, setJson] = useState('')
  const [jsonError, setJsonError] = useState(false)
  const stored = new Set(storedKeys.map(k => k.toLowerCase()))

  const switchTo = (m: 'rows' | 'json') => {
    if (m === mode) return
    if (m === 'json') {
      setJson(rows.some(r => r.key.trim()) ? JSON.stringify(toObject(rows), null, 2) : '')
      setJsonError(false); onJsonError?.(false)
    } else if (jsonError) {
      return // fix the JSON first; switching would lose it
    }
    setMode(m)
  }
  const set = (i: number, patch: Partial<KvRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Segmented<'rows' | 'json'>
          value={mode} onChange={switchTo} label={keyLabel}
          options={[{ value: 'rows', label: t('connectors.mcp.modeRows') }, { value: 'json', label: t('connectors.mcp.modeJson') }]}
        />
      </div>
      {mode === 'json' ? (
        <div>
          <textarea
            aria-label={`${keyLabel} (JSON)`}
            spellCheck={false}
            rows={Math.min(10, Math.max(4, json.split('\n').length + 1))}
            className="w-full rounded-[10px] border border-cream-300 bg-cream-50 px-3 py-2 font-mono text-xs text-ink-200 outline-none focus:border-ginger-500"
            placeholder={'{\n  "X-Api-Key": "…"\n}'}
            value={json}
            onChange={e => {
              setJson(e.target.value)
              const parsed = parseKvJson(e.target.value)
              setJsonError(parsed === null); onJsonError?.(parsed === null)
              if (parsed) onChange(parsed)
            }}
          />
          {jsonError
            ? <p className="mt-1 text-xs text-red-700">{t('connectors.mcp.jsonInvalid')}</p>
            : storedKeys.length > 0 && <p className="mt-1 text-xs text-ink-500">{t('connectors.mcp.jsonKeepHint')}</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                aria-label={keyLabel} className="h-9 flex-[2] font-mono text-xs" value={r.key} placeholder={keyPlaceholder}
                onChange={e => set(i, { key: e.target.value })}
              />
              <Input
                aria-label={valueLabel} className="h-9 flex-[3] font-mono text-xs" value={r.value} autoComplete="off"
                type="password"
                placeholder={stored.has(r.key.trim().toLowerCase()) ? t('connectors.mcp.keepValue') : valuePlaceholder}
                onChange={e => set(i, { value: e.target.value })}
              />
              <Button type="button" variant="ghost" size="icon-sm" aria-label={t('connectors.mcp.removeRow')} onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                <IconClose width={16} height={16} />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, { key: '', value: '' }])}>
            <IconPlus width={14} height={14} />{addLabel}
          </Button>
        </div>
      )}
    </div>
  )
}

type Auth = 'none' | 'bearer' | 'oauth'
type Transport = 'auto' | 'streamable-http' | 'sse'

export interface McpPreset { name: string; url: string; auth: Auth; headers?: KvRow[] }

const asString = (v: unknown) => (typeof v === 'string' ? v : '')
const asList = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])

/**
 * Add (no `connector`) or edit (with `connector`) an MCP server. `local` picks the stdio variant for
 * a new server; an existing one keeps its kind.
 */
export function McpServerForm({ connector, local = false, preset, onDone, onCancel }: {
  connector?: Connector; local?: boolean; preset?: McpPreset; onDone(): void; onCancel?(): void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const cfg = connector?.config ?? {}
  const editing = Boolean(connector)
  const isLocal = editing ? cfg.transport === 'stdio' : local
  const storedHeaders = asList(cfg.headerKeys)
  const storedEnv = asList(cfg.envKeys)

  const [name, setName] = useState(connector?.name ?? preset?.name ?? '')
  const [url, setUrl] = useState(asString(cfg.url) || preset?.url || '')
  const [auth, setAuth] = useState<Auth>((asString(cfg.auth) as Auth) || preset?.auth || 'none')
  const [token, setToken] = useState('')
  const [transport, setTransport] = useState<Transport>((editing && !isLocal ? asString(cfg.transport) as Transport : '') || 'auto')
  const [headers, setHeaders] = useState<KvRow[]>(storedHeaders.length ? storedHeaders.map(key => ({ key, value: '' })) : preset?.headers ?? [])
  const [command, setCommand] = useState(asString(cfg.command))
  const [args, setArgs] = useState(asList(cfg.args).join(' '))
  const [env, setEnv] = useState<KvRow[]>(storedEnv.map(key => ({ key, value: '' })))
  const [jsonInvalid, setJsonInvalid] = useState(false)
  const [busy, setBusy] = useState(false)
  const hadToken = editing && cfg.auth === 'bearer'

  const submit = async () => {
    setBusy(true)
    try {
      const params = isLocal
        ? { name, command: command.trim(), args: args.trim() ? args.trim().split(/\s+/) : [], env: toObject(env), ...(editing ? {} : { transport: 'stdio' }) }
        : { name, url, transport, auth, headers: toObject(headers), ...(auth === 'bearer' && (token || !hadToken) ? { token } : {}) }
      const r = editing
        ? await rpc.call<{ authUrl?: string }>('connectors.mcp.update', { connectorId: connector!.id, ...params })
        : await rpc.call<{ authUrl?: string }>('connectors.mcp.add', params)
      if (r.authUrl) return window.location.assign(r.authUrl)
      toast(t(editing ? 'connectors.mcp.saved' : 'connectors.mcp.added'), 'success')
      onDone()
    } catch (err) {
      toast((err as Error).message, 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="space-y-5" onSubmit={e => { e.preventDefault(); void submit() }}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('connectors.mcp.name')}>{id => <Input id={id} required value={name} onChange={e => setName(e.target.value)} placeholder="Notion" />}</Field>
        {isLocal ? (
          <Field label={t('connectors.mcp.command')} hint={t('connectors.mcp.commandHint')}>
            {id => <Input id={id} required className="font-mono" value={command} onChange={e => setCommand(e.target.value)} placeholder="npx" />}
          </Field>
        ) : (
          <Field label={t('connectors.mcp.url')}>
            {id => <Input id={id} type="url" required className="font-mono" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://mcp.example.com/mcp" />}
          </Field>
        )}
      </div>

      {isLocal ? (
        <>
          <Field label={t('connectors.mcp.args')}>
            {id => <Input id={id} className="font-mono" value={args} onChange={e => setArgs(e.target.value)} placeholder="-y @modelcontextprotocol/server-filesystem /workspace" />}
          </Field>
          <section>
            <h3 className="text-[13px] font-semibold text-ink-300">{t('connectors.mcp.env')}</h3>
            <p className="mb-2 text-xs text-ink-500">{t('connectors.mcp.envHint')}</p>
            <KeyValueEditor
              rows={env} onChange={setEnv} storedKeys={storedEnv} onJsonError={setJsonInvalid}
              keyLabel={t('connectors.mcp.envName')} valueLabel={t('connectors.mcp.headerValue')} addLabel={t('connectors.mcp.addVar')}
              keyPlaceholder="API_KEY"
            />
          </section>
        </>
      ) : (
        <>
          <section className="space-y-3">
            <h3 className="text-[13px] font-semibold text-ink-300">{t('connectors.mcp.auth')}</h3>
            <Segmented<Auth>
              value={auth} onChange={setAuth} label={t('connectors.mcp.auth')}
              options={[
                { value: 'none', label: t('connectors.mcp.auth_none') },
                { value: 'bearer', label: t('connectors.mcp.auth_bearer') },
                { value: 'oauth', label: t('connectors.mcp.auth_oauth') }
              ]}
            />
            {auth === 'bearer' && (
              <Field label={t('connectors.mcp.token')} hint={hadToken ? t('connectors.mcp.tokenKeep') : undefined}>
                {id => <Input id={id} type="password" autoComplete="off" required={!hadToken} value={token} onChange={e => setToken(e.target.value)} />}
              </Field>
            )}
            {auth === 'oauth' && <p className="text-xs text-ink-500">{t('connectors.mcp.oauthHint')}</p>}
          </section>

          <section>
            <h3 className="text-[13px] font-semibold text-ink-300">{t('connectors.mcp.headers')}</h3>
            <p className="mb-2 text-xs text-ink-500">{t('connectors.mcp.headersHint')}</p>
            <KeyValueEditor
              rows={headers} onChange={setHeaders} storedKeys={storedHeaders} onJsonError={setJsonInvalid}
              keyLabel={t('connectors.mcp.headerName')} valueLabel={t('connectors.mcp.headerValue')} addLabel={t('connectors.mcp.addHeader')}
              keyPlaceholder="X-Api-Key"
            />
          </section>

          <details className="group rounded-xl border border-line px-4 py-3">
            <summary className="cursor-pointer select-none text-[13px] font-semibold text-ink-300">{t('connectors.mcp.advanced')}</summary>
            <div className="mt-3 max-w-sm">
              <Field label={t('connectors.mcp.transport')}>{id => (
                <Select
                  id={id} value={transport} onChange={v => setTransport(v)}
                  options={[
                    { value: 'auto', label: t('connectors.mcp.transport_auto') },
                    { value: 'streamable-http', label: 'Streamable HTTP' },
                    { value: 'sse', label: 'SSE' },
                  ]}
                />
              )}</Field>
            </div>
          </details>
        </>
      )}

      <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-4">
        {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>}
        <Button type="submit" loading={busy} disabled={jsonInvalid}>
          {editing ? t('connectors.mcp.save') : auth === 'oauth' && !isLocal ? t('connectors.mcp.signIn') : t('connectors.mcp.add')}
        </Button>
      </div>
    </form>
  )
}
